/**
 * Picks the email-ask offer for a blog post from its slug and category.
 *
 * The passive newsletter modal used to pitch one thing on every blog post:
 * "a 12-month care calendar". Site data says a dated, task-specific promise
 * converts far better than a calendar on posts where the reader does not
 * know their grass (the pre-emergent reminder widget vs the generic
 * seasonal pool; obsidian/research/lead-magnet-audit-2026-08.md). This maps
 * a post to the task it is about so the step-1 ask names a date the reader
 * cares about. Null means "no clear task": the modal keeps the calendar ask.
 *
 * Slug keywords win over category keywords because categories are coarse
 * ("Weed Control" holds pre-emergent, weed-and-feed and post-emergent posts,
 * which want three different promises).
 *
 * Analytics: the resolved `key` rides on the modal's impression / yes /
 * signup events as `offer`. It is a NEW property next to `topic`; `topic`
 * keeps its display-phrase value space (see lib/newsletter-topic.ts).
 */
export type ContentOfferKey =
  | "pre-emergent"
  | "feeding"
  | "seeding"
  | "grubs"
  | "weed-spray"

export interface ContentOffer {
  key: ContentOfferKey
  /** Step-1 yes/no ask. Names one dated thing. */
  microAsk: string
  /** Header title, both steps. */
  title: string
  /** Header subtitle, both steps. */
  subtitle: string
  /** Primary button, both steps. */
  buttonText: string
  /** Success-frame heading. */
  successTitle: string
}

const OFFERS: Record<ContentOfferKey, ContentOffer> = {
  "pre-emergent": {
    key: "pre-emergent",
    microAsk: "Want the pre-emergent date for your area, by email?",
    title: "Know your pre-emergent window",
    subtitle: "One email when soil temps in your area hit the crabgrass mark",
    buttonText: "Email me the date",
    successTitle: "Your pre-emergent reminder is set!",
  },
  feeding: {
    key: "feeding",
    microAsk: "Want a reminder the week before your next feeding?",
    title: "Never miss a feeding window",
    subtitle: "A heads-up the week before each application is due in your area",
    buttonText: "Email me the reminders",
    successTitle: "Your feeding reminders are set!",
  },
  seeding: {
    key: "seeding",
    microAsk: "Want your seeding window, with a one-week heads-up?",
    title: "Seed in the right week",
    subtitle: "Your area's seeding window, emailed a week before it opens",
    buttonText: "Email me the window",
    successTitle: "Your seeding reminder is set!",
  },
  grubs: {
    key: "grubs",
    microAsk: "Want a reminder when grub and insect treatment is due?",
    title: "Treat grubs before the damage",
    subtitle: "One email when the treatment window opens in your area",
    buttonText: "Email me the reminder",
    successTitle: "Your treatment reminder is set!",
  },
  "weed-spray": {
    key: "weed-spray",
    microAsk: "Want the best spraying window for your area, by email?",
    title: "Spray weeds when it works",
    subtitle: "One email when temps and growth in your area favor a spray",
    buttonText: "Email me the window",
    successTitle: "Your spray-window reminder is set!",
  },
}

/** Ordered: first match wins, so the more specific tasks come first. */
const SLUG_RULES: Array<[ContentOfferKey, RegExp]> = [
  ["pre-emergent", /pre-?emergent|crabgrass-preventer|halts|prodiamine|dimension|barricade/],
  ["seeding", /overseed|seeding|reseed|grass-seed|germinat/],
  ["grubs", /grub|chinch|webworm|armyworm|billbug|insect/],
  ["feeding", /weed-and-feed|weed-feed|fertiliz|turf-builder|lawn-food|milorganite|\b10-10-10\b|4-step|triple-action|ironite|starter-fert/],
  ["weed-spray", /herbicide|weed-killer|weedclear|weed-stop|weed-b-gon|tenacity|roundup|groundclear|nutsedge|doveweed|dallisgrass|creeping-charlie|clover|dandelion|get-rid-of|weed-control|post-emergent/],
]

const CATEGORY_RULES: Array<[ContentOfferKey, RegExp]> = [
  ["seeding", /seed|overseed|germinat/],
  ["grubs", /grub|insect|pest/],
  ["feeding", /fertiliz|nutrient/],
  ["weed-spray", /weed|herbicide/],
]

export function getContentOffer(slug?: string | null, category?: string | null): ContentOffer | null {
  const s = (slug ?? "").toLowerCase()
  if (s) {
    for (const [key, re] of SLUG_RULES) {
      if (re.test(s)) return OFFERS[key]
    }
  }
  const c = (category ?? "").toLowerCase()
  if (c) {
    for (const [key, re] of CATEGORY_RULES) {
      if (re.test(c)) return OFFERS[key]
    }
  }
  return null
}
